import React, {useEffect, useMemo, useRef, useState} from 'react'
import {useDispatch, useSelector} from 'react-redux'
import {RootState} from '../../rootReducer'
import PromotionListForModel from './PromotionListForModel'
import {AppDispatch, fetchCarBySlug, FetchData} from '../../thunk'
import addTrailingSlash from '../../../utils/url/addTrailingSlash'
import {QueryParams} from '../../../common/configuration'
import SingleModelInterface from '../../../types/singleModelInterface'

export interface MultiModelPromotionListProps {
    modelSlugs: string[]
    onModelsResolved?: (models: SingleModelInterface[]) => void
    onStatusChange?: (status: {
        hasResolvedPromotions: boolean
        hasAnyPromotions: boolean
        modelNames: string[]
    }) => void
}

export function MultiModelPromotionList({
    modelSlugs,
    onModelsResolved,
    onStatusChange,
}: MultiModelPromotionListProps): React.JSX.Element {
    const dispatch = useDispatch<AppDispatch>()

    const backendUrl = useSelector(
        (state: RootState) => state.general.backendUrl
    )
    const apiQueryParams = useSelector(
        (state: RootState) => state.general.apiQueryParams
    )
    const previewAPIToken = useSelector(
        (state: RootState) => state.general.previewAPIToken
    )
    const dealerCode = useSelector(
        (state: RootState) => state.dealerData.dealerCode
    )

    const modelsWithActivePromotions = useSelector(
        (state: RootState) => state.general.modelsWithActivePromotions
    )

    const storeModelsBySlug = useMemo(() => {
        const bySlug = new Map<string, SingleModelInterface>()
        modelsWithActivePromotions?.forEach((m: SingleModelInterface) => {
            bySlug.set(m.slug, m)
        })

        return bySlug
    }, [modelsWithActivePromotions])

    const [fetchedModelsBySlug, setFetchedModelsBySlug] = useState<
        Record<string, SingleModelInterface>
    >({})
    const [failedSlugs, setFailedSlugs] = useState<Record<string, true>>({})

    const inFlightSlugs = useRef<Set<string>>(new Set())
    const slugsToResolve = useMemo(
        () => Array.from(new Set(modelSlugs.filter(Boolean))),
        [modelSlugs]
    )

    useEffect(() => {
        if (
            !backendUrl ||
            !Array.isArray(slugsToResolve) ||
            slugsToResolve.length === 0
        ) {
            return
        }

        for (const slug of slugsToResolve) {
            if (storeModelsBySlug.has(slug)) {
                continue
            }
            if (slug in fetchedModelsBySlug) {
                continue
            }
            if (slug in failedSlugs) {
                continue
            }
            if (inFlightSlugs.current.has(slug)) {
                continue
            }

            inFlightSlugs.current.add(slug)

            const base = `${addTrailingSlash(backendUrl)}cars/${slug}`
            const withDealer = dealerCode ? `${base}/${dealerCode}` : base
            const endpoint =
                apiQueryParams && previewAPIToken
                    ? `${withDealer}?${QueryParams.PREVIEW_DATE}=${apiQueryParams}`
                    : withDealer

            const req: FetchData = {
                ENDPOINT: endpoint,
                TOKEN: previewAPIToken ?? '',
            }

            dispatch(fetchCarBySlug(req))
                .unwrap()
                .then((resp: unknown) => {
                    const model = resp as SingleModelInterface
                    if (model && typeof model === 'object' && 'slug' in model) {
                        setFetchedModelsBySlug((prev) => ({
                            ...prev,
                            [slug]: model,
                        }))
                    }
                })
                .catch((err) => {
                    console.error('fetchCarBySlug failed for', slug, err)
                    setFailedSlugs((prev) => ({...prev, [slug]: true}))
                })
                .finally(() => {
                    inFlightSlugs.current.delete(slug)
                })
        }
    }, [
        backendUrl,
        slugsToResolve,
        dealerCode,
        apiQueryParams,
        previewAPIToken,
        storeModelsBySlug,
        dispatch,
        fetchedModelsBySlug,
        failedSlugs,
    ])

    const resolvedModels = useMemo(() => {
        const list: SingleModelInterface[] = []
        for (const slug of modelSlugs) {
            const fromStore = storeModelsBySlug.get(slug)
            // eslint-disable-next-line security/detect-object-injection
            const fromLocal = fetchedModelsBySlug[slug]
            if (fromStore) {
                list.push(fromStore)
            } else if (fromLocal) {
                list.push(fromLocal)
            }
        }

        return list
    }, [modelSlugs, storeModelsBySlug, fetchedModelsBySlug])

    const onResolvedRef = useRef<typeof onModelsResolved>(undefined)
    useEffect(() => {
        onResolvedRef.current = onModelsResolved
    }, [onModelsResolved])

    const lastNotifiedSlugsKey = useRef<string>('')
    useEffect(() => {
        if (!resolvedModels.length) {
            return
        }
        const key = resolvedModels.map((m) => m.slug).join('|')
        if (key === lastNotifiedSlugsKey.current) {
            return
        }
        lastNotifiedSlugsKey.current = key
        onResolvedRef.current?.(resolvedModels)
    }, [resolvedModels])

    const statusCallbackRef = useRef<typeof onStatusChange>(undefined)
    useEffect(() => {
        statusCallbackRef.current = onStatusChange
    }, [onStatusChange])

    useEffect(() => {
        if (!statusCallbackRef.current) {
            return
        }

        const resolvedOrFailedCount = slugsToResolve.filter((slug) => {
            return (
                storeModelsBySlug.has(slug) ||
                slug in fetchedModelsBySlug ||
                slug in failedSlugs
            )
        }).length

        const hasResolvedPromotions =
            slugsToResolve.length > 0 &&
            resolvedOrFailedCount === slugsToResolve.length
        const hasAnyPromotions = resolvedModels.some(
            (model) => (model.related_promotions ?? []).length > 0
        )
        const modelNames = resolvedModels
            .map((model) => model.name)
            .filter(Boolean)

        statusCallbackRef.current({
            hasResolvedPromotions,
            hasAnyPromotions,
            modelNames,
        })
    }, [
        slugsToResolve,
        storeModelsBySlug,
        fetchedModelsBySlug,
        failedSlugs,
        resolvedModels,
    ])

    const modelsWithDedupedPromos = useMemo(() => {
        const globalSeen = new Set<string>()
        const norm = (v: unknown) =>
            typeof v === 'string' ? v.trim() : v ?? ''

        return resolvedModels.map((model) => {
            const localSeen = new Set<string>()

            const filteredPromos = (model.related_promotions ?? []).filter(
                (p) => {
                    const key = JSON.stringify([
                        norm(p.title),
                        norm(p.type),
                        norm(p.sub_title),
                        norm(p.cta_text),
                        norm(p.cta_url),
                        norm(p.price),
                    ])
                    if (localSeen.has(key)) {
                        return true
                    }
                    localSeen.add(key)
                    if (globalSeen.has(key)) {
                        return false
                    }
                    globalSeen.add(key)

                    return true
                }
            )

            return {...model, related_promotions: filteredPromos}
        })
    }, [resolvedModels])

    if (!resolvedModels.length) {
        return <></>
    }

    return (
        <>
            {modelsWithDedupedPromos.map((model) => (
                <PromotionListForModel key={model.slug} model={model} />
            ))}
        </>
    )
}
